// @flow
import * as React from 'react';
import styled from 'styled-components';
import { getCityStateString } from 'utils/address';
import { trackClicked } from 'utils/analytics';
import {
  SEARCH_HIT_IMAGE_WIDTH,
  SEARCH_HIT_IMAGE_WIDTH_SMALL,
  LISTING_LOCATION_TYPE
} from 'utils/constants';
import type { ImageData, RemoteOptions } from 'api/shared';
import Box from 'components/Box';
import Divider from 'components/Divider';
import Icon from 'components/Icon';
import { LogoImage } from 'components/Image';
import { SmallTile } from 'components/Tile';
import { HoverEffect } from 'components/SearchTile';
import {
  SearchTileLink,
  SearchTileContainer,
  getCtaByType,
  StyledButton,
  SecondaryButton,
  ListingRouterLink
} from 'components/SearchHit';
import { Link as RouterLink } from 'components/RouterLink';
import { Tag } from 'components/ListingStatusLabel';
import { H3, BodySmall, BodyMicro, Underline } from 'components/Text';
import ShowModalContainer from 'containers/ShowModalContainer';
import ShareIcon from 'share.svg';
import { colors, shadows, transition, bp } from 'theme';

const StyledRouterLink = styled(RouterLink)`
  color: inherit;
  text-decoration: none;
  transition: all ${transition};
  &:hover {
    color: ${colors.waterBlue};
    text-decoration: none;
  }
`;

const ApplyButton = styled(StyledButton)`
  width: 100%;
  height: auto;
  min-height: 40px;
  font-size: 18px;
  svg {
    width: 16px;
    height: 16px;
    margin-right: 6px;
  }
  ${props =>
    !props.$applied
      ? `
  color: white;
  background: ${colors.waterBlue};
  &:hover {
    background: ${colors.turtleGreen};
    color: white;
  }`
      : `
  color: ${colors.battleshipGrey};
  background: white;;
  box-shadow: 0 0 0 1px ${colors.silver} inset;
  &:hover {
    background: white;
  }
  `}
`;

const ShareButton = ({ listing }: { listing: Object }) => (
  <ShowModalContainer
    render={showModal => (
      <SecondaryButton
        aria-label="Share"
        onClick={e => {
          e.preventDefault();
          showModal('SHARE_LISTING', {
            listingType: 'JOB',
            listing,
            title: `Share This Job`
          });
        }}
      >
        <ShareIcon width="14" height="14" />
        {getText('Share')}
      </SecondaryButton>
    )}
  />
);

const StyledUnderline = styled(Underline)`
  background-position: 0 0.85em;
`;

const ButtonContainer = styled(Box)`
  @media (max-width: ${bp[1] - 1}px) {
    button,
    a {
      width: calc(50% - 8px);
      margin: 0 4px;
      box-shadow: 0 0 0 1px ${colors.silver} inset;
    }
  }
`;

const FeaturedSearchHit = ({
  hit,
  saveListing,
  unsaveListing,
  saved,
  canSave,
  trackClick,
  applications
}: {
  hit: {
    objectID: string,
    name: string,
    type: string,
    actionType: string,
    orgName: string,
    orgUrl?: { [UserLocale]: string },
    city: string,
    state: string,
    stateStr: string,
    country: string,
    isFullTime: boolean,
    remoteOptions: RemoteOptions,
    url: { [UserLocale]: string },
    logo: ImageData
  },
  saveListing?: () => void,
  unsaveListing: () => void,
  trackClick?: () => void,
  saved: boolean,
  canSave: boolean,
  applications?: Array<string>
}) => {
  const {
    objectID: id,
    name,
    type,
    actionType,
    orgName,
    orgUrl,
    city,
    state,
    stateStr,
    country,
    isFullTime,
    remoteOptions,
    url,
    logo
  } = hit;
  const location = getCityStateString({
    city,
    stateCode: state || stateStr,
    country
  });
  const listingInfo = [
    isFullTime ? getText('Full Time') : getText('Part Time'),
    LISTING_LOCATION_TYPE[remoteOptions.locationType]
  ];
  const userHasApplied = !!(applications && applications.includes(id));
  const cta = getCtaByType({
    type,
    actionType,
    baseUrl: url[USER_LOCALE],
    userHasApplied
  });

  return (
    <SearchTileContainer position="relative" my={10}>
      <SearchTileLink
        aria-hidden="true"
        url={url}
        trackClick={trackClick}
        isFeatured
      />
      <SmallTile
        overflow="visible !important"
        pr={[undefined, null, '16px !important']}
        boxShadow={`${shadows.high} !important`}
      >
        <Box display={['block', 'flex']} flexWrap={['wrap', null, 'nowrap']}>
          <Box flex="0 0 auto" pr={[0, 32]} mb={[32, 0]}>
            <LogoImage
              data-qa-id="logo-uploaded-image"
              alignItems="flex-start"
              width={[
                SEARCH_HIT_IMAGE_WIDTH_SMALL,
                null,
                null,
                null,
                SEARCH_HIT_IMAGE_WIDTH
              ]}
              fsWidth={SEARCH_HIT_IMAGE_WIDTH}
              handle={logo ? logo.handle : undefined}
              mimetype={logo ? logo.mimetype : undefined}
              alt={name}
              initial={orgName}
            />
          </Box>
          <Box
            width={['auto', null, '100%']}
            flex="1 1 auto"
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <ListingRouterLink
              to={{
                pathname: url[USER_LOCALE],
                state: {
                  showSearchContext: true,
                  originatingPathname: window.location.pathname,
                  isFeatured: true
                }
              }}
              onClick={() => {
                if (trackClick) trackClick();
              }}
            >
              <H3 my={0} lineHeight={1.125}>
                <StyledUnderline>{name}</StyledUnderline>
              </H3>
            </ListingRouterLink>
            <HoverEffect />
            <BodySmall mt="4px" mb={10} lineHeight={1.5}>
              <strong>
                {orgUrl ? (
                  <StyledRouterLink
                    to={orgUrl[USER_LOCALE]}
                    onClick={e => {
                      e.stopPropagation();
                    }}
                    data-qa-id="search-result-org-link"
                  >
                    {orgName}
                  </StyledRouterLink>
                ) : (
                  orgName
                )}
              </strong>
              {location && (
                <>
                  <Box mx="4px" display="inline">
                    {' | '}
                  </Box>
                  <Box display="inline-block">{location}</Box>
                </>
              )}
            </BodySmall>
            <BodyMicro color={colors.battleshipGrey}>
              <Box display="inline" mr={16}>
                {listingInfo.map((info, index) => (
                  <React.Fragment key={info}>
                    {index > 0 && (
                      <Box
                        display="inline-block"
                        mx="8px"
                        my="-0.2em"
                        bg="currentColor"
                        height="1em"
                        width="1px"
                      />
                    )}
                    {info}
                  </React.Fragment>
                ))}
              </Box>
              <Tag color={colors.charcoalGrey} bg={colors.aluminum}>
                {getText('Promoted')}
              </Tag>
            </BodyMicro>
          </Box>

          <Box
            zIndex="2"
            width={['100%', null, 225, 150, 225]}
            flex="0 0 auto"
            flexWrap="wrap"
            ml={[0, null, 12]}
            mb={[0, null, -14]}
            mt={[36, null, -14]}
            pl={[0, null, 16]}
            display="flex"
            alignItems="center"
            borderLeft={`1px solid ${colors.aluminum}`}
          >
            <Box
              display="flex"
              width={1}
              flexDirection={['column', 'row', 'column']}
              justifyContent={['flex-start', null, 'center']}
              pointerEvents="all"
            >
              <Box width={[1, 1 / 2, 1]} pr={[0, '4px', 0]} flex="0 0 auto">
                <ApplyButton
                  to={{
                    pathname: cta.url,
                    state: {
                      showSearchContext: true,
                      originatingPathname: window.location.pathname,
                      isFeatured: true
                    }
                  }}
                  $applied={userHasApplied}
                  component={RouterLink}
                  onClick={() => {
                    trackClicked('Search results CTA button', {
                      type,
                      result_is_promoted: true
                    });
                  }}
                >
                  {cta.text}
                </ApplyButton>
              </Box>

              <ButtonContainer
                width={['calc(100% + 8px)', 'calc(50% + 4px)', 1]}
                mt={['8px', 0, 12]}
                mr={['-4px', null, 0]}
                ml={['-4px', 0]}
                display={['flex', USER_LOCALE === 'en' ? 'flex' : 'block']}
                alignItems="center"
              >
                <ShareButton listing={hit} />
                {canSave && (
                  <>
                    {USER_LOCALE === 'en' ? (
                      <Box
                        display={['none', null, 'block']}
                        mx="-1px"
                        height={16}
                        width="1px"
                        bg={colors.aluminum}
                      />
                    ) : (
                      <Divider light my={0} />
                    )}
                    <SecondaryButton
                      ml={[16, null, 0]}
                      saved={saved}
                      onClick={e => {
                        e.stopPropagation();
                        if (saved) {
                          unsaveListing();
                        } else if (saveListing) {
                          saveListing();
                        }
                      }}
                      data-qa-id={saved ? 'unsave-listing' : 'save-listing'}
                    >
                      <Icon name="heart" width={13} height={13} />
                      {saved ? getText('Unsave') : getText('Save')}
                    </SecondaryButton>
                  </>
                )}
              </ButtonContainer>
            </Box>
          </Box>
        </Box>
      </SmallTile>
    </SearchTileContainer>
  );
};

export default FeaturedSearchHit;
